import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { chain, difference, map } from 'lodash';
import { availablePageSizes } from 'config';
import { BankFilePageResponseWithSummaries, bankFilePaymentsAPI } from 'redux/bankFilePaymentsAPI';
import { createPaginationAdapter } from '@payer-admin-ui/components-v2/src/state/paginationAdapter';
import { createFilterAdapter } from '@payer-admin-ui/components-v2/src/state/filterAdapter';
import { patch } from '@payer-admin-ui/components-v2/src/functions/lodashExtensions';
import { SingleSortType } from '@payer-admin-ui/components-v2/src/types/state';
import { MerchantAdminState } from 'redux/store';
import { createMultiColumnSortingAdapter } from '@payer-admin-ui/components-v2/src/state/sortingAdapter';
import { clearTableSettings, updateTableSettings } from 'redux/userSettings';
import { SortDirection } from '@payer-admin-ui/components/src/Table/types';
import {
  BankFilePayment,
  BankFilePaymentResponse,
  GetDetailedBankFilePaymentResponse,
} from '@payer-admin-ui/api/src/bankfilepayments/bankFilePaymentTypes';
import {
  BankFilePaymentEntity,
  BankFilePaymentFilters,
  BankFilePaymentsGlobalState,
  BankFilePaymentsGridSortableColumn,
  BankFilePaymentsSortableColumn,
  bankFilePaymentsSortMappings,
} from './types/bankFilePaymentState';

export const BANK_FILE_PAYMENTS_TABLE_SETTINGS_KEY = 'bankFilePayments';

export const fetchBankFilePayment = createAsyncThunk<
  GetDetailedBankFilePaymentResponse,
  { bankFilePaymentId: string },
  { state: MerchantAdminState }
>('bankFilePayments/fetchById', async ({ bankFilePaymentId }: { bankFilePaymentId: string }) => {
  return bankFilePaymentsAPI.bankFilePayments.fetchById({ bankFilePaymentId });
});

export const fetchBankFilePayments = createAsyncThunk<
  BankFilePageResponseWithSummaries,
  void,
  { state: MerchantAdminState }
>('bankFilePayments/fetchPage', async (_, { getState }) => {
  const { page, pageSize, appliedFilters, filterOverride, sortState } = getState().bankFilePayments.global.listView;
  const sort = (sortState || []).filter(s => s.sortDirection).map(s => `${s.column}|${s.sortDirection.toUpperCase()}`);
  const effectiveFilters = filterOverride ?? appliedFilters;
  return bankFilePaymentsAPI.bankFilePayments.fetchPage({
    page,
    pageSize,
    filters: effectiveFilters,
    sort,
  });
});

export const setBackoffice = createAsyncThunk(
  'bankFilePayments/setBackoffice',
  async ({ bankFilePaymentId, merchantComment }: { bankFilePaymentId: string; merchantComment: string }) => {
    return bankFilePaymentsAPI.bankFilePayments.setBackoffice({ bankFilePaymentId, merchantComment });
  },
);

export const applyBankFilePaymentsFilters = createAsyncThunk<void, void, { state: MerchantAdminState }>(
  'bankFilePayments/applyBankFilePaymentsFilters',
  async (_, { getState, dispatch }) => {
    dispatch(applyFilters());
    dispatch(
      updateTableSettings({
        component: BANK_FILE_PAYMENTS_TABLE_SETTINGS_KEY,
        update: { appliedFilters: getState().bankFilePayments.global.listView.appliedFilters },
      }),
    );
  },
);

export const setBankFilePaymentsSort = createAsyncThunk<
  void,
  {
    columnKey: BankFilePaymentsGridSortableColumn;
    direction?: SortDirection;
  },
  { state: MerchantAdminState }
>('bankFilePayments/setBankFilePaymentsSort', async ({ columnKey, direction }, { getState, dispatch }) => {
  const cols = bankFilePaymentsSortMappings[columnKey];
  cols.forEach(c => dispatch(setSort({ column: c, sortDirection: direction as any })));
  const sortModel = getState().bankFilePayments.global.listView.sortState;
  dispatch(
    updateTableSettings({
      component: BANK_FILE_PAYMENTS_TABLE_SETTINGS_KEY,
      update: { sortOrders: sortModel },
    }),
  );
  dispatch(fetchBankFilePayments());
});

export const syncUserSettings = createAsyncThunk<void, void, { state: MerchantAdminState }>(
  'bankFilePayments/syncUserSettings',
  async (_, { getState, dispatch }) => {
    const settings = getState().userSettings?.tableSettings?.[BANK_FILE_PAYMENTS_TABLE_SETTINGS_KEY];
    const pageSize = settings?.pageSize;
    const sortModel = settings?.sortOrders;
    const appliedFilters = settings?.appliedFilters;
    if (pageSize) {
      dispatch(setPageSize(pageSize));
    }
    if (sortModel) {
      dispatch(setSorts(sortModel as SingleSortType<BankFilePaymentsSortableColumn>[]));
    }
    dispatch(
      setFilter({
        ...getState().bankFilePayments.global.listView.filters,
        ...appliedFilters,
      }),
    );
    dispatch(applyFilters());
    dispatch(setInited());
  },
);

export const clearBankFilePaymentsTableSettings = createAsyncThunk<void, void, { state: MerchantAdminState }>(
  'bankFilePayments/clearBankFilePaymentsTableSettings',
  async (_, { dispatch }) => {
    dispatch(setFilter(initialFilters));
    dispatch(applyFilters());
    dispatch(setSorts([]));
    dispatch(setPageSize(availablePageSizes[0]));
    dispatch(
      clearTableSettings({
        component: BANK_FILE_PAYMENTS_TABLE_SETTINGS_KEY,
        customDefaultSettings: {
          appliedFilters: initialFilters,
        },
      }),
    );
  },
);

const SINGLE_CACHE_SIZE = 10;

export const emptyFilters: BankFilePaymentFilters = {
  bankAccountTypes: [],
  processingDateStart: '',
  processingDateEnd: '',
  bookingDateStart: '',
  bookingDateEnd: '',
  name: '',
  id: '',
  fullyMapped: [],
  manuallyMapped: null,
  toPayer: [],
  doNotMap: [],
  search: '',
  doNotMapReasons: [],
  customer: '',
  fundSourceTypes: [],
};

const entityAdapter = createEntityAdapter<BankFilePaymentEntity>();

const paginationAdapter = createPaginationAdapter<BankFilePaymentsGlobalState>({
  selectState: (state: BankFilePaymentsGlobalState) => state.listView,
});
const filterAdapter = createFilterAdapter<BankFilePaymentsGlobalState, BankFilePaymentFilters>({
  selectState: (state: BankFilePaymentsGlobalState) => state.listView,
  emptyFilters,
  reset: paginationAdapter.reset,
});

const initialFilters: BankFilePaymentFilters = {
  ...emptyFilters,
  fullyMapped: ['false'],
  toPayer: ['false'],
  doNotMap: ['false'],
  bankAccountTypes: [],
  fundSourceTypes: ['PAYMENT_TO_ACCOUNT'],
};

const sortingAdapter = createMultiColumnSortingAdapter<BankFilePaymentsGlobalState, BankFilePaymentsSortableColumn>({
  selectState: (state: BankFilePaymentsGlobalState) => state.listView,
  reset: paginationAdapter.reset,
});

const initialState: BankFilePaymentsGlobalState = entityAdapter.getInitialState({
  singleView: {
    cachedIds: [],
  },
  listView: {
    isFetching: false,
    error: null,
    inited: false,
    totalCount: 0,
    ids: [],
    page: 0,
    pageSize: availablePageSizes[3],
    summary: undefined,
    filters: initialFilters,
    appliedFilters: initialFilters,
    sortState: undefined,
    filterOverride: null,
  },
});

const pruneCache = (state: BankFilePaymentsGlobalState) => {
  entityAdapter.removeMany(state, difference(state.ids, state.singleView.cachedIds, state.listView.ids));
};

const upsertOnePruneCache = (state: BankFilePaymentsGlobalState, entity: BankFilePaymentEntity) => {
  entityAdapter.upsertOne(state, entity);
  state.singleView.cachedIds = chain([entity.id]).union(state.singleView.cachedIds).take(SINGLE_CACHE_SIZE).value();
  pruneCache(state);
};

const bankFilePaymentsSlice = createSlice({
  name: 'bankFilePayments',
  initialState,
  reducers: {
    resetError: state => {
      state.listView.error = null;
    },
    setInited: state => {
      state.listView.inited = true;
    },
    setFilterOverride: (state, { payload }: { payload: BankFilePaymentFilters }) => {
      state.listView.filterOverride = payload;
      state.listView.page = 0;
    },
    clearFilterOverride: state => {
      state.listView.filterOverride = null;
    },
    setPage: paginationAdapter.setPage,
    setPageSize: paginationAdapter.setPageSize,
    setFilter: filterAdapter.setFilter,
    clearFilter: filterAdapter.clearFilter,
    clearAllFilters: filterAdapter.clearAllFilters,
    applyFilters: filterAdapter.applyFilters,
    setSort: sortingAdapter.setSort,
    setSorts: sortingAdapter.setSorts,
    resetSort: sortingAdapter.resetSort,
    resetSingleViewError: (state, { payload: { bankFilePaymentId } }: { payload: { bankFilePaymentId: string } }) => {
      entityAdapter.upsertOne(state, {
        id: bankFilePaymentId,
        entity: undefined,
        isFetching: false,
        error: null,
        backoffice: { isFetching: false, error: null },
      });
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchBankFilePayment.pending, (state, { meta: { arg } }) => {
      upsertOnePruneCache(state, {
        id: arg.bankFilePaymentId,
        isFetching: true,
        error: null,
      });
    });
    builder.addCase(fetchBankFilePayment.fulfilled, (state, { meta: { arg }, payload: bankFilePayment }) => {
      const { bankFilePaymentHeader, ...rest } = bankFilePayment;
      const entity: BankFilePayment = {
        ...bankFilePaymentHeader,
        ...rest,
      };
      entityAdapter.upsertOne(state, {
        id: arg.bankFilePaymentId,
        entity,
        isFetching: false,
        error: null,
      });
    });
    builder.addCase(fetchBankFilePayment.rejected, (state, { meta: { arg }, error }) => {
      entityAdapter.upsertOne(state, {
        id: arg.bankFilePaymentId,
        entity: undefined,
        isFetching: false,
        error,
      });
    });
    builder.addCase(fetchBankFilePayments.pending, (state, _) => {
      patch(state.listView, {
        isFetching: true,
        error: null,
      });
    });
    builder.addCase(
      fetchBankFilePayments.fulfilled,
      (state, { payload: { contents, totalCount, summaryByCurrency } }) => {
        const bankFilePayments = normalize(contents);
        patch(state.listView, {
          ids: map(bankFilePayments, 'id'),
          totalCount,
          isFetching: false,
          summary: summaryByCurrency,
        });
        entityAdapter.upsertMany(state, bankFilePayments);
        pruneCache(state);
      },
    );
    builder.addCase(fetchBankFilePayments.rejected, (state, { error }) => {
      patch(state.listView, {
        ids: [],
        totalCount: 0,
        isFetching: false,
        summary: undefined,
        error,
      });
      pruneCache(state);
    });
    builder.addCase(setBackoffice.pending, (state, { meta: { arg } }) => {
      upsertOnePruneCache(state, {
        id: arg.bankFilePaymentId,
        entity: undefined,
        isFetching: false,
        error: null,
        backoffice: { isFetching: true, error: null },
      });
    });
    builder.addCase(setBackoffice.fulfilled, (state, { meta: { arg }, payload: bankFilePayment }) => {
      const { bankFilePaymentHeader, ...rest } = bankFilePayment;
      const entity: BankFilePayment = {
        ...bankFilePaymentHeader,
        ...rest,
      };
      entityAdapter.upsertOne(state, {
        id: arg.bankFilePaymentId,
        entity,
        isFetching: false,
        error: null,
        backoffice: { isFetching: false, error: null },
      });
    });
    builder.addCase(setBackoffice.rejected, (state, { meta: { arg }, error }) => {
      entityAdapter.upsertOne(state, {
        id: arg.bankFilePaymentId,
        entity: undefined,
        isFetching: false,
        error: null,
        backoffice: { isFetching: false, error },
      });
    });
  },
});

const normalize = (bankFilePayments: BankFilePaymentResponse[]): BankFilePaymentEntity[] =>
  map(bankFilePayments, bankFilePayment => ({
    id: bankFilePayment.id.toString(),
    entity: bankFilePayment,
    isFetching: false,
    error: null,
    backoffice: { isFetching: false, error: null },
  }));

export const {
  actions: {
    resetError,
    resetSingleViewError,
    setPage,
    setPageSize,
    setFilter,
    clearAllFilters,
    clearFilter,
    applyFilters,
    setInited,
    setSort,
    setSorts,
    resetSort,
    setFilterOverride,
    clearFilterOverride,
  },
  reducer: bankFilePaymentsReducer,
} = bankFilePaymentsSlice;

export const bankFilePaymentsSelectors = entityAdapter.getSelectors((state: any) => state.bankFilePayments.global);
